import Text from "components/Text/Text"
import styles from './BaseballCard.module.scss';
import { ReactComponent as MailIcon } from "assets/email.svg";
import { ReactComponent as SummaryIcon } from "assets/summary.svg";
import { ReactComponent as DetailsIcon } from "assets/details.svg";
import { ReactComponent as CheckInIcon } from "assets/check_in.svg";
import { ReactComponent as NotesIcon } from "assets/notes.svg";
import { ReactComponent as NavigationArrowIcon } from "assets/navigationArrowRight.svg";
import { ReactComponent as StatsIcon } from "assets/stats.svg";
//import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { ReactComponent as MoneyIcon } from "assets/CurrencyCircleDollar.svg";
import { ReactComponent as InfoIcon } from 'assets/info.svg';
import { ReactComponent as LeftIcon } from "assets/caretleft.svg";
import { validateAddressForGoogleMaps } from "helpers/addressValidation";

import SendTab, { SendTabProps } from "./components/SendTab";
import React, { useRef } from "react";
import Summary from "./modules/Summary";
import Notes from "./modules/Notes";
import CheckIn from "./modules/CheckIn";
import ListCustomers from "Modules/MapRouter/modules/ListCustomers";
import { store } from "stores/Store";
import { date2usDotDate } from "helpers";
import PractitionerDetails from "./modules/Details";
import Opportunities from "Modules/Opportunities";
import useExternalApiService from "helpers/ExternalApiService";
import Stats from "./modules/Stats";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { getLeadStateByPractitioner } from "helpers/lead";
import RoutesTab from './components/RoutesTab';
import Toastr from "components/Toastr";

import { logEvent } from '@amplitude/analytics-browser'; // ← browser SDK
import FollowUpMenu from "./components/FollowUpMenu";
import EditFollowUpDateMenu from "./components/EditFollowUpDateMenu";
import ListCustomersTab from "Modules/MapRouter/modules/ListCustomers/ListCustomersTab";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";

export enum Tab {
  SUMMARY = "summary",
  DETAILS = "details",
  CHECK_IN = "check_in",
  NOTES = "notes",
  OPPORTUNITIES = "opportunities",
  STATS = "stats",
  PRACTITIONERS_LIST = "practitioners_list"
}

const BaseballCard = observer(({ tab, generalNotes, subTab, backHandler }: { tab: Tab, generalNotes: string, subTab?: string, backHandler?: () => void }) => {
  const [selectedTab, setSelectedTab] = React.useState<Tab>(tab);
  const [isOpen, setIsOpen] = React.useState(false);
  const [showGrade, setShowGrade] = React.useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = React.useState<boolean>(false);
  const [distance, setDistance] = React.useState<number | null>(null);
  const [isCalculatingDistance, setIsCalculatingDistance] = React.useState<boolean>(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = React.useState(false);
  const [isDraftLoading, setIsDraftLoading] = React.useState(false);
  const [draftError, setDraftError] = React.useState<string>();
  const [draftResult, setDraftResult] = React.useState<{ subject: string, body: string }>();
  const [showToastr, setShowToastr] = React.useState(false);
  const [selectedTabListCustomers, setSelectedTabListCustomers] = React.useState<ListCustomersTab>(ListCustomersTab.ALL);
  const [backTabListCustomers, setBackTabListCustomers] = React.useState<ListCustomersTab>(ListCustomersTab.ALL);
  const [isFollowUpMenuOpen, setIsFollowUpMenuOpen] = React.useState(false);
  const [isEditFollowUpDateMenuOpen, setIsEditFollowUpDateMenuOpen] = React.useState(false);
  const [isLoadingFollowUpDates, setIsLoadingFollowUpDates] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [isDeletingLead, setIsDeletingLead] = React.useState(false);

  const draftAbortControllerRef = React.useRef<AbortController | null>(null);
  const fetchFollowUpsAbortControllerRef = useRef<AbortController | null>(null);

  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { getData, postData } = useExternalApiService(apiUrl);


  const defaultAddress = store.currentPractitioner?.addresses
    ?.find((a: any) => a.id === store.currentPractitioner?.default_address);
  // Pull lat & lon out once so our JSX can read them
  const lat = defaultAddress?.latitude;
  const lon = defaultAddress?.longitude;


  React.useEffect(() => {
    if (store.checkIn?.practitioner_name) {
      setCurrentPractitioner();
    }
    fetchFollowUpDates();
  }, [])

  React.useEffect(() => {
    if (defaultAddress) {
      setIsCalculatingDistance(true);
      /*
      calculteDistance()?.then((distance: number | null) => {
        if (distance !== null) {
          setDistance(distance);
        }
        setIsCalculatingDistance(false);
      }).catch(() => {
        setIsCalculatingDistance(false);
      });
      */
      const promise = calculteDistance();
      if (!promise) {
        // calculteDistance() returned null for some reason—bail out:
        setIsCalculatingDistance(false);
        return;
      }

      promise
        .then((d) => {
          if (d !== null) {
            setDistance(d);
          }
        })
        .catch(() => {
          // geolocation was denied or failed
        })
        .finally(() => {
          setIsCalculatingDistance(false);
        });
        //trackClick("View practitioner");
    }
  }, [defaultAddress]);

  // Helper to track all button clicks with a consistent event
  const trackClick = (section: string) => {
    const label = 'Baseballcard: ' + section
    logEvent(label, { section });
  };

  const calculteDistance = () => {
    if (!lat || !lon) {
      return null;
    }

    // Get current position from browser
    if ("geolocation" in navigator) {
      return new Promise<number>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition((position) => {
          const lat1 = position.coords.latitude;
          const lon1 = position.coords.longitude;
          const lat2 = lat;
          const lon2 = lon;

          // Calculate distance using Haversine formula
          const R = 6371; // Radius of earth in km
          const dLat = (lat2 - lat1) * Math.PI / 180;
          const dLon = (lon2 - lon1) * Math.PI / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const distance = R * c;

          resolve(Math.round(distance * 0.621371)); // Convert to miles and round
        }, (error) => {
          console.log("erro" ,error);
          reject(error);
        });
      });
    }

    return null;
  }
  const setCurrentPractitioner = async () => {
    const result = await postData(`/api/get_practitioner_by_primary_name`, {
      "practitioner_name": store.checkIn?.practitioner_name
    });
    store.setCurrentPractitioner({ ...result.practitioner, general_notes: generalNotes });
  }

  const fetchFollowUpDates = React.useCallback(async () => {
    if (!store.currentPractitioner?.id) return;

    setIsLoadingFollowUpDates(true);

    const abortController = new AbortController();
    fetchFollowUpsAbortControllerRef.current = abortController;

    store.setCurrentPractitionerLastCheckinDate(null);

    try {
      const response = await getData(`/api/practitioner/${store.currentPractitioner?.id}/followups?netsuite_id=${store.currentPractitioner?.netsuite_id ?? ''}&bg_id=${store.currentPractitioner?.bg_rowid ?? ''}`, abortController.signal);
      store.setCurrentPractitioner({
        ...store.currentPractitioner,
      });
      store.setCurrentPractitionerLastCheckinDate(response.last_checkin_date);
      store.setCurrentPractitionerNextFollowUpDate(response.next_followup_date);

      setIsLoadingFollowUpDates(false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }

      setIsLoadingFollowUpDates(false);
    }
  }, [getData]);

  const handleSetFollowUpDate = async (date: string) => {
    await postData(`/api/practitioner/${store.currentPractitioner?.id}/followup`, { followup_date: date });
    setIsFollowUpMenuOpen(false);
    setIsEditFollowUpDateMenuOpen(false);

    fetchFollowUpDates();
  };

  const handleCompleteFollowUp = async () => {
    await postData(`/api/practitioner/${store.currentPractitioner?.id}/followup/complete`);

    fetchFollowUpDates();
  };

  const handleClearFollowUpDate = async () => {
    await postData(`/api/practitioner/${store.currentPractitioner?.id}/followup/clear`);
    setIsFollowUpMenuOpen(false);
    setIsEditFollowUpDateMenuOpen(false);

    fetchFollowUpDates();
  };

  const handleContact: SendTabProps["onSelect"] = async (opt, bulletPoints, useRecentCheckingNotes, useAccountNotes) => {
    switch (opt) {
      case 'email-self':
        window.open(`mailto:${store.currentPractitioner?.email}`, '_blank');
        setIsOpen(false);
        break;
      case 'email-draft': {
        setIsDraftLoading(true);
        setDraftError(undefined);
        const abortController = new AbortController();
        draftAbortControllerRef.current = abortController;
        try {
          const result = await postData(`/api/winback_draft_email_to_practitioner`, {
            "practitioner_id": store.currentPractitioner?.id ?? '',
            "bullet_points": bulletPoints,
            "use_recent_checking_notes": useRecentCheckingNotes,
            "use_account_notes": useAccountNotes,
            "fmc_name": store.impersonatedUserName  // Performance: avoids extra DB query
          }, abortController.signal);
          setDraftResult(result);
        } catch (e: any) {
          if (e.name !== 'AbortError') {
            setDraftError('Failed to generate email draft. Please try again.');
          }
        } finally {
          setIsDraftLoading(false);
          draftAbortControllerRef.current = null;
        }
        break;
      }
      case 'phone':
        window.open(`tel:${store.currentPractitioner?.phone}`, '_blank');
        setIsOpen(false);
        break;
      case 'website':
        window.open(`${store.currentPractitioner?.website}`, '_blank');
        setIsOpen(false);
        break;
    }
  }

  const handleDraftRequestCancelation = () => {
    if (draftAbortControllerRef.current) {
      draftAbortControllerRef.current.abort();
      draftAbortControllerRef.current = null;
    }
  };

  const handleOpenDraftLink = () => {
    if (!draftResult) {
      return;
    }

    const subject = encodeURIComponent(draftResult.subject);
    const body = encodeURIComponent(draftResult.body);
    window.open(`mailto:${store.currentPractitioner?.email}?subject=${subject}&body=${body}`, '_blank');
    setIsOpen(false);
    setDraftResult(undefined);
  };

  React.useEffect(() => {
    if (selectedTab === Tab.PRACTITIONERS_LIST) {
      setIsHeaderHidden(true);
    } else {
      setIsHeaderHidden(false);
    }

    if (selectedTab) {
      trackClick(`${selectedTab} tab`);
      console.log("tracking ", `${selectedTab}`);
    }
  }, [selectedTab])

  React.useEffect(() => {
    if (store.currentPractitioner?.id) {
      fetchFollowUpDates();
    }

    return () => {
      fetchFollowUpsAbortControllerRef.current?.abort();
      fetchFollowUpsAbortControllerRef.current = null;
      setIsLoadingFollowUpDates(false);
    };
  }, [store.currentPractitioner?.id]);

  const renderTabContent = () => {
    switch (selectedTab) {
      case Tab.SUMMARY:
        return (
          <Summary
            isLoadingFollowUpDates={isLoadingFollowUpDates}
            navigate={setSelectedTab}
            onOpenFollowUpMenu={() => setIsFollowUpMenuOpen(true)}
            onClickEditFollowUpDate={() => setIsEditFollowUpDateMenuOpen(true)}
            onCompleteFollowUp={handleCompleteFollowUp}
          />
        );
      case Tab.NOTES:
        return <Notes />
      case Tab.CHECK_IN:
        return <CheckIn setIsHeaderHidden={setIsHeaderHidden} onCheckinAdded={fetchFollowUpDates} />
      case Tab.PRACTITIONERS_LIST:
        return (
          <ListCustomers
            onCustomerClick={onCustomerSelected}
            selectedTab={selectedTabListCustomers}
            onSelectTab={setSelectedTabListCustomers}
            onPractitionerAddedToRoute={handlePractitionerAdded}
          />
        );
      case Tab.DETAILS:
        return <PractitionerDetails navigate={setSelectedTab} />
      case Tab.STATS:        
        return <Stats />
      default:        
        return <Opportunities />
    }
  }

  const onCustomerSelected = (customer: any, selectedTab: Tab = Tab.SUMMARY, backTabListCustomers: ListCustomersTab = ListCustomersTab.ALL) => {
    store.setCurrentPractitioner({ ...customer, general_notes: '' });
    setSelectedTab(selectedTab);
    setBackTabListCustomers(backTabListCustomers);
  }

  const handleRoutes = (opt: string) => {
    if (!defaultAddress) return;

    const address = defaultAddress?.address || '';
    const coordinates = `${defaultAddress?.latitude},${defaultAddress?.longitude}`;

    switch (opt) {
      case 'google':
        // Validate and clean address for Google Maps search
        if (address) {
          const { isValid, cleanedAddress } = validateAddressForGoogleMaps(address);
          if (isValid && cleanedAddress) {
            // Use cleaned address search if valid
            const encodedAddress = encodeURIComponent(cleanedAddress);
            window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
          } else if (defaultAddress?.latitude && defaultAddress?.longitude) {
            // Fallback to coordinates if address is malformed
            window.open(`https://www.google.com/maps/search/?api=1&query=${coordinates}`, '_blank');
          } else {
            console.log('No valid address or coordinates for Google Maps navigation');
          }
        } else if (defaultAddress?.latitude && defaultAddress?.longitude) {
          // No address at all, use coordinates
          window.open(`https://www.google.com/maps/search/?api=1&query=${coordinates}`, '_blank');
        } else {
          console.log('No valid address or coordinates for Google Maps navigation');
        }
        break;
      case 'waze':
        if (defaultAddress?.latitude && defaultAddress?.longitude) {
          window.open(`https://waze.com/ul?ll=${defaultAddress.latitude},${defaultAddress.longitude}&navigate=yes`, '_blank');
        } else {
          console.log('No coordinates available for Waze navigation');
        }
        break;
      case 'apple':
        // For Apple Maps, try cleaned address first, then coordinates
        if (address) {
          const { isValid, cleanedAddress } = validateAddressForGoogleMaps(address);
          if (isValid && cleanedAddress) {
            const encodedAddress = encodeURIComponent(cleanedAddress);
            window.open(`maps://?q=${encodedAddress}`, '_blank');
          } else if (defaultAddress?.latitude && defaultAddress?.longitude) {
            window.open(`maps://?q=${coordinates}`, '_blank');
          } else {
            console.log('No valid address or coordinates for Apple Maps navigation');
          }
        } else if (defaultAddress?.latitude && defaultAddress?.longitude) {
          window.open(`maps://?q=${coordinates}`, '_blank');
        } else {
          console.log('No valid address or coordinates for Apple Maps navigation');
        }
        break;
    }
    setIsGoRouteModalOpen(false);
  };

  const handlePractitionerAdded = () => {
    setShowToastr(true);
  };

  const handleDeleteLead = async () => {
    setIsDeletingLead(true);
    try {
      await postData(`/api/delete_practitioner_from_chat`, {
        practitioner_id: store.currentPractitioner?.id
      });

      // Track deletion event
      logEvent('Baseballcard: Delete Lead', {
        practitioner_id: store.currentPractitioner?.id,
        practitioner_name: store.currentPractitioner?.name
      });

      // Clear leads cache to refresh the list
      store.clearLeadsCache();

      // Clear current practitioner
      store.setCurrentPractitioner(null);

      // Navigate back to practitioners list with Leads tab selected
      setSelectedTab(Tab.PRACTITIONERS_LIST);
      setSelectedTabListCustomers(ListCustomersTab.LEAD);
    } catch (error) {
      console.error('Failed to delete lead:', error);
      // Don't show the "added to route" message on error - just log it
    } finally {
      setIsDeletingLead(false);
      setIsDeleteModalOpen(false);
    }
  };

  const isEditable = ((store.currentPractitioner?.status === "LEAD-READY2SUBMIT")  || (store.currentPractitioner.status === "LEAD-INCOMPLETE"))

  return (
    <div>
      {(!isHeaderHidden) && (
        <div className={getLeadStateByPractitioner(store.currentPractitioner)  ? styles["tinted-border"] : ""}>
          <div>{getLeadStateByPractitioner(store.currentPractitioner)}</div>
        </div>
      )}
      <div className={styles.wrapper}>
        {(!isHeaderHidden) &&
          <>
            {/*<ExpandIcon style={{ transform: 'rotate(180deg)' }} onClick={
              backHandler ? () =>backHandler() : () => setSelectedTab(Tab.PRACTITIONERS_LIST)
            } 
            width={22} height={22}             
            />*/}
            <LeftIcon 
              onClick={
                backHandler ? () => backHandler() : () => {
                  setSelectedTab(Tab.PRACTITIONERS_LIST)
                  setSelectedTabListCustomers(backTabListCustomers);
                }
              } 
              className={styles.leftIcon} />
            <div className={styles.headerWrapper}>
              <div>
                {/*<Text type="header">{store.currentPractitioner?.name}</Text>*/}
                {/*<ArrowLeft fill='black' onClick={() => setSelectedTab(Tab.PRACTITIONERS_LIST)}></ArrowLeft>*/}
                <Text type="header">{store.currentPractitioner?.primary_name}</Text>
                <Text type="subHeader">{store.currentPractitioner?.specialty}</Text>
                <Text width={'140px'} type="subHeader">{`${defaultAddress?.city}, ${defaultAddress?.state}`}</Text>
                <div className={styles.locationWrapper} onClick={() => setIsGoRouteModalOpen(true)}>
                  {/*<Text type="subHeader" width={'100px'}>{`${isCalculatingDistance ? 'Calculating...' : distance ? `${distance} miles away` : 'Distance unavailable'}`}</Text>*/}
                  {isCalculatingDistance
                    ? (<Text type="subHeader" width={"100px"}>Calculating…</Text>)
                    : lat == null || lon == null
                      ? (<div className={styles.missingDistanceWrapper}>
                            <Text type="subHeader" width={"110px"}>Missing distance</Text>
                             <div className={styles.tooltipContainer}>
                              <InfoIcon width={14} height={14} style={{ 
                                marginLeft:0,  
                                padding:0,
                                display:'block'    
                                }} 
                              />
                              <span className={styles.tooltipText}>Latitude & Longitude Missing</span>
                            </div>
                         </div>
                        )
                      : distance == null
                        ? (<div className={styles.missingDistanceWrapper}>
                            <Text type="subHeader" width={"110px"}>Missing distance</Text>
                             <div className={styles.tooltipContainer}>
                              <InfoIcon width={14} height={14} style={{ 
                                marginLeft:0,  
                                padding:0,
                                display:'block'    
                                }} 
                              />
                              <span className={styles.tooltipText}>Did you enable app to use GPS?</span>
                            </div>
                         </div>
                        )
                        : (
                          <div className={styles.distanceWithIcon} onClick={() => setIsGoRouteModalOpen(true)}>
                            <Text type="subHeader" width={"100px"}>{`${distance} miles away`}</Text>
                            <NavigationArrowIcon width={20} height={20} />
                          </div>
                          )
                  }
                  
                  
                </div>

                {isEditable ? (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button
                      onClick={() => {
                        navigate(`/?${new URLSearchParams({showNewLead: 'true', toggle: "false", extraArgs: "edit"}).toString()}`);
                      }}
                      className={styles["edit-lead-button"]}>
                      EDIT LEAD
                    </button>
                    <button
                      onClick={() => setIsDeleteModalOpen(true)}
                      className={styles["delete-lead-button"]}
                      disabled={isDeletingLead}>
                      DELETE LEAD
                    </button>
                  </div>
                ) : ""}
                {/*
                <Text type="subHeader">{`Last contact: ${date2usDotDate(store.currentPractitioner?.last_contact)}`}</Text>
                */}
              </div>

              <div className={styles.columnWrapper}>
                <div onClick={() => setShowGrade(!showGrade)}
                  className={`${styles.gradeWrapper} ${store.currentPractitioner?.grade === 'A' ? styles.gradeA :
                    store.currentPractitioner?.grade === 'B' ? styles.gradeB :
                      store.currentPractitioner?.grade === 'C' ? styles.gradeC :
                        store.currentPractitioner?.grade === 'D' ? styles.gradeD : styles.gradeNone
                    }`}
                >
                  {showGrade ?
                    <div className={styles.gradeWrapper}>
                      <p className={styles.gradeText}>{store.currentPractitioner?.grade}</p>
                      <p className={styles.gradeText__small}>Grade</p>
                    </div> :
                    <img height='130px' src={store.currentPractitioner?.picture} alt="" />
                  }
                  
                </div>
                <MailIcon onClick={() => setIsOpen(true)} style={{ cursor: 'pointer', paddingTop: '10px' }} />
                <SendTab
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  onSelect={handleContact}
                  isLoading={isDraftLoading}
                  error={draftError}
                  draftResult={draftResult}
                  cancelDraftRequest={handleDraftRequestCancelation}
                  openDraftLink={handleOpenDraftLink}
                />
              </div>
            </div>
            <div className={styles.horizontalScrollWrapper}>
              <div
                onClick={() => setSelectedTab(Tab.SUMMARY)}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.SUMMARY ? styles.selectedTab : styles.disabledTab}`}
              >
                <SummaryIcon />
                <p>Summary</p>
              </div>
              <div
                onClick={() => setSelectedTab(Tab.DETAILS)}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.DETAILS ? styles.selectedTab : styles.disabledTab}`}
              >
                <DetailsIcon />
                <p>Details</p>
              </div>
              <div
                onClick={() => setSelectedTab(Tab.CHECK_IN)}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.CHECK_IN ? styles.selectedTab : styles.disabledTab}`}
              >
                <CheckInIcon />
                <p>Check-In</p>
              </div>
              <div
                onClick={() => setSelectedTab(Tab.NOTES)}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.NOTES ? styles.selectedTab : styles.disabledTab}`}
              >
                <NotesIcon />
                <p>Notes</p>
              </div>
              <div
                onClick={() => setSelectedTab(Tab.OPPORTUNITIES)}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.OPPORTUNITIES ? styles.selectedTab : styles.disabledTab}`}
              >
                <MoneyIcon />
                <p>Opp/Risk</p>
              </div>
              <div
                onClick={() => setSelectedTab(Tab.STATS)}
                className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.STATS ? styles.selectedTab : styles.disabledTab}`}
              >
                <StatsIcon />
                <p>Stats</p>
              </div>
            </div>

          </>
        }
        <div className={styles.tabContentWrapper}>
          {renderTabContent()}
        </div>
        <RoutesTab isOpen={isGoRouteModalOpen} setIsOpen={setIsGoRouteModalOpen} onSelect={handleRoutes} onPractitionerAdded={handlePractitionerAdded} />

        <Toastr 
          message="Practitioner added to route" 
          isVisible={showToastr} 
          onClose={() => setShowToastr(false)}
          backgroundColor="white"
          closeTime={2000}
        />
      </div>

      <FollowUpMenu
        isOpen={isFollowUpMenuOpen}
        onClose={() => setIsFollowUpMenuOpen(false)}
        onSubmit={handleSetFollowUpDate}
        onClear={handleClearFollowUpDate}
        customer={store.currentPractitioner}
      />

      <EditFollowUpDateMenu
        isOpen={isEditFollowUpDateMenuOpen}
        onClose={() => setIsEditFollowUpDateMenuOpen(false)}
        onSubmit={handleSetFollowUpDate}
        onClear={handleClearFollowUpDate}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteLead}
        title="Delete Lead"
        message={`Are you sure you want to delete ${store.currentPractitioner?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
});


export default BaseballCard;